import { yupResolver } from '@hookform/resolvers/yup'
import { maxMessageLength, maxMessageLengthWithConfirmation } from '@shared/core/events'
import { MESSAGE_TEMPLATE_MESSAGE_BODY_MAX, MESSAGE_TEMPLATE_VARCHAR_MAX } from '@shared/core/message-templates'
import type { UserPreferencesResponse } from '@shared/core/preferences'
import type { Resolver } from 'react-hook-form'
import type { UserData } from 'src/modules/event/utils/message-builder'
import { generateEstimatedEventMessage } from 'src/modules/event/utils/message-builder'
import * as yup from 'yup'

export interface ReminderSettingsFormData {
  sendReminders: boolean
  from: string
  location: string
  reminderMessage: string
  timing: string[]
  appointmentConfirmation: boolean
}

const varcharTooLong = `Must be at most ${MESSAGE_TEMPLATE_VARCHAR_MAX} characters`

export function createBookingReminderSettingsSchema(profile: UserData, preferences: UserPreferencesResponse) {
  return yup.object({
    sendReminders: yup.boolean().required(),
    from: yup.string().max(MESSAGE_TEMPLATE_VARCHAR_MAX, varcharTooLong),
    location: yup.string().max(MESSAGE_TEMPLATE_VARCHAR_MAX, varcharTooLong),
    reminderMessage: yup
      .string()
      .required('Reminder message is required')
      .max(MESSAGE_TEMPLATE_MESSAGE_BODY_MAX, `Must be at most ${MESSAGE_TEMPLATE_MESSAGE_BODY_MAX} characters`)
      .test('rendered-sms-length', function (value) {
        const parent = this.parent as ReminderSettingsFormData
        const { messageLength } = generateEstimatedEventMessage(
          'reminder',
          {
            ...profile,
            appointment_confirmation: parent.appointmentConfirmation,
            default_message_template: {
              ...profile.default_message_template,
              sender_name: parent.from,
              location: parent.location,
              reminder_message: value,
            },
          },
          preferences,
          profile.default_message_template?.language,
        )
        if (parent.appointmentConfirmation && messageLength > maxMessageLengthWithConfirmation) {
          return this.createError({
            message: 'Default SMS Reminder must be shorter than 239 characters',
          })
        }
        if (messageLength > maxMessageLength) {
          return this.createError({
            message: 'Default SMS Reminder must be shorter than 320 characters',
          })
        }
        return true
      }),
    timing: yup.array().of(yup.string().required()).min(1, 'At least one timing is required').required(),
    appointmentConfirmation: yup.boolean().required(),
  })
}

export function getBookingReminderSettingsResolver(
  profile: UserData | undefined,
  preferences: UserPreferencesResponse | undefined,
): Resolver<ReminderSettingsFormData> {
  if (!profile || !preferences) {
    return async (values) => ({
      values,
      errors: {},
    })
  }
  return yupResolver(createBookingReminderSettingsSchema(profile, preferences)) as Resolver<ReminderSettingsFormData>
}
